import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import type { CalendarEventItem, CalendarEventStatus } from '@/types'
import * as calendarApi from '../../services/api/calendar'

type CalendarView = 'month' | 'week' | 'day'

interface CalendarFilters {
  sources: string[]
  statuses: CalendarEventStatus[]
}

interface CalendarState {
  eventsById: Record<string, CalendarEventItem>
  eventIds: string[]
  loading: boolean
  error: string | null
  currentView: CalendarView
  selectedDate: string
  filters: CalendarFilters
}

const initialState: CalendarState = {
  eventsById: {},
  eventIds: [],
  loading: false,
  error: null,
  currentView: 'month',
  selectedDate: new Date().toISOString(),
  filters: {
    sources: [],
    statuses: [],
  },
}

interface FetchCalendarParams {
  startDate?: string
  endDate?: string
  sources?: string[]
  projectId?: string
}

const resolveErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) {
      return message
    }
  }
  return fallback
}

export const fetchCalendarEvents = createAsyncThunk<
  CalendarEventItem[],
  FetchCalendarParams | undefined,
  { rejectValue: string }
>('calendar/fetchEvents', async (params, thunkAPI) => {
  try {
    const response = await calendarApi.getEvents(params)
    return response.data?.events ?? []
  } catch (error: unknown) {
    return thunkAPI.rejectWithValue(resolveErrorMessage(error, '載入日曆事件失敗'))
  }
})

const sortEvents = (events: CalendarEventItem[]) =>
  [...events].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
  )

const calendarSlice = createSlice({
  name: 'calendar',
  initialState,
  reducers: {
    upsertEvents(state, action: PayloadAction<CalendarEventItem[]>) {
      action.payload.forEach(event => {
        state.eventsById[event.id] = event
      })
      state.eventIds = sortEvents(Object.values(state.eventsById)).map(event => event.id)
    },
    updateEventStatus(
      state,
      action: PayloadAction<{ formInstanceId: string; status: CalendarEventStatus }>,
    ) {
      const { formInstanceId, status } = action.payload
      Object.values(state.eventsById).forEach(event => {
        if (event.formInstanceId === formInstanceId) {
          state.eventsById[event.id] = { ...event, status }
        }
      })
      state.eventIds = sortEvents(Object.values(state.eventsById)).map(event => event.id)
    },
    setView(state, action: PayloadAction<CalendarView>) {
      state.currentView = action.payload
    },
    setSelectedDate(state, action: PayloadAction<string>) {
      state.selectedDate = action.payload
    },
    setFilters(state, action: PayloadAction<Partial<CalendarFilters>>) {
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
    },
    clearCalendar(state) {
      state.eventsById = {}
      state.eventIds = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCalendarEvents.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCalendarEvents.fulfilled, (state, action) => {
        state.loading = false
        action.payload.forEach(event => {
          state.eventsById[event.id] = event
        })
        state.eventIds = sortEvents(Object.values(state.eventsById)).map(event => event.id)
      })
      .addCase(fetchCalendarEvents.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || '載入日曆事件失敗'
      })
  },
})

export const {
  upsertEvents,
  updateEventStatus,
  setView,
  setSelectedDate,
  setFilters,
  clearCalendar,
} = calendarSlice.actions

export default calendarSlice.reducer
